import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Tabs,
} from 'antd';
import { EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { mappingApi } from '../services/api/mapping';

const { Title } = Typography;
const { TabPane } = Tabs;

interface DocTypeDetail {
  id: string;
  name: string;
  script: string;
  parent?: string;
  dataFields: any[];
  options: any[];
}

const DocTypeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [docType, setDocType] = useState<DocTypeDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadDocType();
    }
  }, [id]);

  const loadDocType = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await mappingApi.getDocType(id);
      setDocType(response.data);
    } catch (error) {
      console.error('Failed to load DocType:', error);
      message.error('Failed to load DocType');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!docType) {
    return (
      <div style={{ padding: '24px' }}>
        <Title level={2}>DocType not found</Title>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/doctypes')}
          >
            Back to List
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/doctype/${id}/edit`)}
          >
            Edit
          </Button>
        </Space>

        <Card>
          <Title level={2}>{docType.name}</Title>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{docType.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{docType.name}</Descriptions.Item>
            <Descriptions.Item label="Parent">
              {docType.parent || 'None'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Tabs defaultActiveKey="script">
          <TabPane tab="Script" key="script">
            <Card>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '600px',
                }}
              >
                {docType.script}
              </pre>
            </Card>
          </TabPane>

          <TabPane
            tab={`Data Fields (${docType.dataFields?.length || 0})`}
            key="dataFields"
          >
            <Card>
              {docType.dataFields?.map((field, index) => (
                <Card
                  key={field.id || index}
                  type="inner"
                  title={field.displayName || field.fieldName}
                  style={{ marginBottom: '16px' }}
                >
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Field Name">
                      {field.fieldName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Type">
                      {field.dataType?.name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Default Page">
                      {field.defaultPageNo}
                    </Descriptions.Item>
                    <Descriptions.Item label="Required">
                      {field.required ? 'Yes' : 'No'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </Card>
          </TabPane>

          <TabPane
            tab={`Options (${docType.options?.length || 0})`}
            key="options"
          >
            <Card>
              {docType.options?.map((option, index) => (
                <Card
                  key={option.id || index}
                  type="inner"
                  title={option.displayName || option.name}
                  style={{ marginBottom: '16px' }}
                >
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Name">
                      {option.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Type">
                      {option.dataType?.name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Advanced">
                      {option.advanced ? 'Yes' : 'No'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </Card>
          </TabPane>
        </Tabs>
      </Space>
    </div>
  );
};

export default DocTypeDetail;
